import { StageDateCell } from './StageDateCell';
import type { StageDateEntry } from '../hooks/useStageDateEditing';

export type StageRow = {
  id: string;
  stage_name: string;
  stage_order?: number;
  project_type?: string;
};

type StageTableProps = {
  allStages: StageRow[];
  currentStage?: { id: string; stage_name: string } | null;
  stageDates: Record<string, StageDateEntry>;
  setStageDates: React.Dispatch<
    React.SetStateAction<Record<string, StageDateEntry>>
  >;
  today: string;
  handleStageDateBlur: (
    stageName: string,
    field: 'start' | 'completion'
  ) => void;
  handleClearStageDate: (
    stageName: string,
    field: 'start' | 'completion'
  ) => void;
  handleStageDateSetAndSave: (
    stageName: string,
    field: 'start' | 'completion',
    value: string
  ) => void;
};

/**
 * "Stage of Construction" — the start/complete date grid the inspector fills
 * on every visit. Shared by the question flow and the manual-upload path
 * (JIM-0904-11: "I need to stay to the construction on there").
 */
export function StageTable({
  allStages,
  currentStage,
  stageDates,
  setStageDates,
  today,
  handleStageDateBlur,
  handleClearStageDate,
  handleStageDateSetAndSave,
}: StageTableProps) {
  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-700">
        <h2 className="font-semibold text-slate-900 dark:text-white">
          Stage of Construction
        </h2>
        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
          Tap a date cell to set today's date. Tap the date to edit.
        </p>
      </div>
      {/* Jim 2026-10-08: on a phone two 110 px date columns left the name ~56 px
          ("BMP Ins…"); below `sm` the name takes its own line and the two date
          cells sit beneath it. */}
      <div className="grid grid-cols-2 sm:grid-cols-[1fr_132px_132px] gap-2 px-4 py-2 bg-slate-50 dark:bg-slate-700/50 border-b border-slate-100 dark:border-slate-700 text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">
        <span className="col-span-2 sm:col-span-1">Stage</span>
        <span className="text-center">Start</span>
        <span className="text-center">Complete</span>
      </div>
      <div className="divide-y divide-slate-100 dark:divide-slate-700">
        {allStages.map((stage) => {
          const entry = stageDates[stage.stage_name];
          const isCurrent = currentStage?.id === stage.id;
          return (
            <div
              key={stage.id}
              className={`grid grid-cols-2 sm:grid-cols-[1fr_132px_132px] gap-x-2 gap-y-1.5 sm:gap-2 px-4 py-3 items-center ${isCurrent ? 'bg-primary-50 dark:bg-primary-900/10' : ''}`}
            >
              <div className="col-span-2 sm:col-span-1 flex items-center gap-2 min-w-0">
                {isCurrent && (
                  <span className="flex-shrink-0 w-2 h-2 rounded-full bg-primary-500" />
                )}
                <span
                  // Jim 2026-10-08: "BMP Ins…" on an iPhone — the name wraps, never truncates.
                  className={`text-sm font-medium leading-snug break-words ${isCurrent ? 'text-primary-700 dark:text-primary-300' : 'text-slate-700 dark:text-slate-300'}`}
                >
                  {stage.stage_name}
                </span>
              </div>
              <div>
                <StageDateCell
                  value={entry?.start || ''}
                  clearLabel="Clear start date"
                  todayClassName="w-full text-xs text-center text-primary-600 dark:text-primary-400 border border-dashed border-primary-300 dark:border-primary-700 rounded py-1 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
                  onChange={(value) =>
                    setStageDates((prev) => ({
                      ...prev,
                      [stage.stage_name]: {
                        ...(prev[stage.stage_name] || {}),
                        start: value,
                      },
                    }))
                  }
                  onBlur={() => handleStageDateBlur(stage.stage_name, 'start')}
                  onClear={() =>
                    handleClearStageDate(stage.stage_name, 'start')
                  }
                  onSetToday={() =>
                    handleStageDateSetAndSave(stage.stage_name, 'start', today)
                  }
                />
              </div>
              <div>
                <StageDateCell
                  value={entry?.completion || ''}
                  clearLabel="Clear completion date"
                  todayClassName="w-full text-xs text-center text-slate-400 dark:text-slate-500 border border-dashed border-slate-300 dark:border-slate-600 rounded py-1 hover:text-primary-600 hover:border-primary-300 dark:hover:text-primary-400 transition-colors"
                  onChange={(value) =>
                    setStageDates((prev) => ({
                      ...prev,
                      [stage.stage_name]: {
                        ...(prev[stage.stage_name] || {}),
                        completion: value,
                      },
                    }))
                  }
                  onBlur={() =>
                    handleStageDateBlur(stage.stage_name, 'completion')
                  }
                  onClear={() =>
                    handleClearStageDate(stage.stage_name, 'completion')
                  }
                  onSetToday={() =>
                    handleStageDateSetAndSave(
                      stage.stage_name,
                      'completion',
                      today
                    )
                  }
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
